import React, { useState, useEffect, useRef } from 'react'
import {
  Grid, LinearProgress, MenuItem, Button, FormControl, Select, Box, Typography, Stack, Input, DialogTitle,
  Dialog, DialogContent, Autocomplete, createFilterOptions, TextField, IconButton, Tooltip, CircularProgress,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import stylesheet from '../detail-container.stylesheet'
import { ClassNameMap, withStyles } from '@mui/styles'
import { useSelector } from 'react-redux'
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import { RootState, useAppThunkDispatch } from '../../../../redux/store'
import { PatientService } from '../../../../services/patient.service'
import Helper from '../../../../utils/helper'
import { toast } from 'react-toastify'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'
import { useUpdateDataAction } from '../../../../utils/update-details-hook'
import Draggable from 'react-draggable'
import DialogTitleHeaderComponent from '../common/dialog-title-header.component'
import SiteListComponent from '../info-header/site-list.component'
import PatientProgramTasksComponent from '../../../tasks/patient-program-tasks.component'
import { setSelectedTask } from '../../../../redux/common-data-slice'
import { formatDisplayName } from '../../../../utils/display-name-formatter'
import { isControlVisible } from '../comp-config'

interface IProps {
  open: boolean,
  input: any,
  handleDialogClose: any,
  classes: ClassNameMap
}

const UploadTestResultComponent: React.FC<IProps> = ({ open, input, handleDialogClose, classes }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isAddSite, setIsAddSite] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null) as any
  const [testsList, setTestsList] = useState([]) as any
  const [selectedSite, setSelectedSite] = useState(input?.site || null) as any
  const [syncingSite, setSyncingSite] = useState(false)
  const [pushingOrder, setPushingOrder] = useState(false)
  const [resultData, setResultData] = useState({
    status: input?.mark_status
      ? input?.mark_status
      : input?.status || ''
  }) as any
  const [note, setNote] = useState(input?.test_notes || '') as any
  const [isNoteFocused, setIsNoteFocused] = useState(false);
  const [dateRecorded, setDateRecorded] = useState(
    input?.date_recorded
      ? moment.unix(input?.date_recorded).format('YYYY-MM-DD')
      : moment().format('YYYY-MM-DD')
  ) as any
  const [resultDate, setResultDate] = useState({
    date: '',
    errMsg: '',
  }) as any
  const [scheduledDate, setScheduledDate] = useState({
    date: (input?.scheduled_date)
      ? moment.unix(input?.scheduled_date).format('YYYY-MM-DD')
      : '',
    errMsg: ''
  }) as any
  const [completedDate, SetCompletedDate] = useState({
    date: (input?.order_completed_date) ? moment.unix(input?.order_completed_date).format('YYYY-MM-DD') : '',
    errMsg: ''
  }) as any
  const [dateError, setDateError] = useState('');
  const [insuranceStatus, setInsuranceStatus] = useState(
    input?.insurance_approval_status
      ? input.insurance_approval_status
      : ''
  ) as any
  const [source, setSource] = useState(input?.result_source || '') as any
  const [measurement, setMeasurement] = useState([] as any)
  const [newRecordList, setNewRecordList] = useState([] as any)
  const [showOptionalMeasurements, setShowOptionalMeasurements] = useState(false)

  const dispatch = useAppThunkDispatch()
  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const uiResource = useSelector((state: RootState) => state.metaData.metadata)
  const patientService = new PatientService()
  const selectedTask = useSelector((state: RootState) => state.commonData.selectedTask)
  const { updatePatientData } = useUpdateDataAction(patientDetail.id)
  const uploadResultRef = useRef(null) as any

  useEffect(() => {
    if (!isLoading) {
      getTestLists()

      if (input.id && input.test_name && ([input.status, input.mark_status].includes('results_available')))
        getMeasurementByTest(input.test_name)
    }
  }, [input])

  const getTestLists = () => {
    setIsLoading(true)
    patientService.getTestsData().then((res: any) => {
      setTestsList(res?.data || [])
      setIsLoading(false)
    }).catch((error: any) => {
      setIsLoading(false)
      toast.error(Helper.getError(error), Helper.bottom_center())
    })
  }

  const closeUpdateTest = () => {
    setIsLoading(false)
    handleDialogClose()
  }

  const postTestResult = (payload: any) => {
    setIsLoading(true)
    patientDetail.id && patientService.putDiagnosticTestResult(patientDetail.id, payload).then((res: any) => {
      if (res?.data)
        updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
      closeUpdateTest()
    }).catch((error: any) => {
      closeUpdateTest()
      toast.error(Helper.getError(error), Helper.bottom_center())
    })
  }

  const handleSubmit = (e: any) => {
    e.preventDefault()
    if ((!input && input.title !== 'Add result')) return;

    if (['results_available'].includes(resultData.status)) {
      const utxtimes = moment(dateRecorded).format('YYYY-MM-DD HH:mm:ss')
      const date_recorded = moment(utxtimes).format('X')

      const hasMeasurementValues = (newRecordList || []).some((item: any) => item?.value !== '' && item?.value != null)
      if (hasMeasurementValues && !resultDate.date) {
        setResultDate({ date: '', errMsg: 'Result date is required' })
        toast.error('Result date is required when entering measurement values', Helper.bottom_center())
        return
      }
      const result_date_recorded = resultDate.date
        ? moment(moment(resultDate.date).format('YYYY-MM-DD HH:mm:ss')).format('X')
        : date_recorded

      const meaList =
        newRecordList
          ?.map((item: any) => ({
            kbm_id: item?.id,
            name: item?.name,
            source_type: item?.source_type,
            unit: item?.unit || item?.default_unit,
            value: item?.value || '',
            tag: item?.tag && Object.keys(item.tag).length > 0 ? item.tag : null,
            date_recorded: result_date_recorded,
          }))
          ?.filter((item: any) => item.value !== '') || []

      let basePayload = {
        test_name: input.id ? input.test_name : resultData.test_name,
        date_recorded: date_recorded,
        result_source: source,
      } as any

      if (input?.enrollment_id) basePayload = { ...basePayload, enrollment_id: input.enrollment_id }

      if (input?.id) basePayload = { ...basePayload, test_id: input.id }

      if (note !== (input?.test_notes || '')) basePayload = { ...basePayload, test_notes: note }

      // Add scheduled_date if changed
      const schDate = scheduledDate?.date ? moment(scheduledDate?.date, 'YYYY-MM-DD').unix() : ''
      if (schDate !== input?.scheduled_date) basePayload = { ...basePayload, scheduled_date: schDate }

      // Add order_completed_date if changed
      const resDate = completedDate?.date ? moment(completedDate.date, 'YYYY-MM-DD').unix() : ''
      if (resDate !== input?.order_completed_date && resDate !== '') basePayload = { ...basePayload, order_completed_date: resDate }

      const formData = new FormData()
      Object.keys(basePayload).map((key: any, value: any) => {
        formData.append(key, basePayload[key] || '')
      })

      if (selectedFile) {
        formData.append('file', selectedFile)
        formData.append('date', date_recorded)
      }
      if (meaList.length > 0) {
        formData.append('measurements', JSON.stringify(meaList))
      }
      postTestResult(formData)
    } else {
      let payload = {} as any
      if (input.title !== 'Add result') payload = { ...payload, test_id: input.id }
      if (note !== (input?.test_notes || '')) payload = { ...payload, test_notes: note }

      if (resultData.status !== input?.status) payload = { ...payload, status: resultData.status }

      // Send site_id if selected, or null to clear if site was previously set
      if (selectedSite?.id) {
        payload = { ...payload, site_id: Number(selectedSite.id) }
      } else if (input?.site?.id && !selectedSite) {
        // Site was cleared - send null to remove it
        payload = { ...payload, site_id: null }
      }

      if (insuranceStatus !== input?.insurance_approval_status) payload = { ...payload, insurance_status: insuranceStatus }

      // Only send dates if they have changed and have a value
      // Send null explicitly if user cleared a previously set date
      if (completedDate?.date) {
        const resDate = moment(completedDate.date, 'YYYY-MM-DD').unix()
        if (resDate !== input?.order_completed_date) payload = { ...payload, order_completed_date: resDate }
      } else if (input?.order_completed_date) {
        // User cleared a previously set date - send null to clear it
        payload = { ...payload, order_completed_date: null }
      }

      if (scheduledDate?.date) {
        const schDate = moment(scheduledDate.date, 'YYYY-MM-DD').unix()
        if (schDate !== input?.scheduled_date) payload = { ...payload, scheduled_date: schDate }
      } else if (input?.scheduled_date) {
        // User cleared a previously set date - send null to clear it
        payload = { ...payload, scheduled_date: null }
      }

      if (dateRecorded && moment(dateRecorded, 'YYYY-MM-DD').unix() !== input?.date_recorded) payload = { ...payload, date_recorded: moment(dateRecorded, 'YYYY-MM-DD').unix() }

      Object.keys(payload)?.length === 1 && payload?.id && delete payload.id

      if (Object.keys(payload)?.length > 0 && input.id) {
        patientService
          .putDiagnosticTest(input.id, payload)
          .then((res: any) => {
            setIsLoading(true)
            if (res?.data) updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
            closeUpdateTest()
          })
          .catch((error: any) => {
            closeUpdateTest()
            toast.error(Helper.getError(error), Helper.bottom_center())
          })
      } else closeUpdateTest()
    } 
  }

  const isDisabled = () => {
    return (
      isLoading ||
      (scheduledDate.date && scheduledDate.errMsg !== '') ||
      (Helper.isAIDA() && ['order_preparation', 'ordered', 'order_notified', 'order_scheduled', 'order_follow_up'].includes(resultData.status) && !selectedSite) ||
      ((resultData.status === 'results_available' || input.status === 'results_available') && (resultData.name === '' || !dateRecorded || dateError !== ''))
    )
  }

  const validateDate = (d: any, name: any) => {
    const currDate = d ? moment(d).format('YYYY-MM-DD') : ''

    let errorMessage = ''
    if (currDate === '') {
      errorMessage = ''
    } else if (currDate === 'Invalid date') {
      errorMessage = 'Please select valid date'
    }

    let date = currDate
    let errMsg = ''
    if (currDate === '')
      date = ''
    else if (errorMessage)
      errMsg = errorMessage

    if (name === 'scheduledDate')
      setScheduledDate({ date: date, errMsg: errMsg })
    else if (name === 'completedDate')
      SetCompletedDate({ date: date, errMsg: errMsg })
    else if (name === 'resultDate')
      setResultDate({ date: date, errMsg: errMsg })
    else {
      setDateRecorded(date)
      setDateError(errMsg)
    }
  }

  const handleFileChange = (event: any) => setSelectedFile(event.target.files[0]);

  const handleSyncSiteToElation = async () => {
    if (!selectedSite?.id || syncingSite) return
    setSyncingSite(true)
    try {
      // Pass patient ID to add site to patient's care team in Elation
      const response = await patientService.syncSiteToElation(selectedSite.id, patientDetail?.id)
      const data = response.data
      if (data.success) {
        const message = data.added_to_team
          ? data.message
          : `${data.message} (not added to patient's care team)`
        toast.success(message, Helper.bottom_center())
      } else {
        toast.warning(data.message || 'Site sync completed with warnings', Helper.bottom_center())
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.error?.message || 'Failed to sync site to Elation'
      toast.error(errorMsg, Helper.bottom_center())
    } finally {
      setSyncingSite(false)
    }
  }

  const handlePushOrderToElation = async () => {
    if (!input?.id || pushingOrder) return
    setPushingOrder(true)
    try {
      const response = await patientService.pushTestOrderToElation(input.id)
      const data = response.data
      toast.success(data?.message || 'Draft order created in Elation', Helper.bottom_center())
    } catch (error: any) {
      const errorMsg = error.response?.data?.error?.message || 'Failed to create Elation order'
      toast.error(errorMsg, Helper.bottom_center())
    } finally {
      setPushingOrder(false)
    }
  }

  const getMeasurementByTest = (name: any) => {
    const prgName = (patientDetail?.program_enrollments
      ?.filter((m: any) => m.selected && m.status === 'active' && m.program_type === "evaluation")
      .map((e: any) => e.program)
      .join(',')) || ''

    name && patientService.getMeasurementsByTest(name, prgName, input?.enrollment_id).then((response: any) => {
      // Sort: needed measurements first (priority='needed' tagged by backend when
      // patient_id + enrollment_id are provided), then optional, alpha within each.
      let measurementList = response?.data?.sort((a: any, b: any) => {
        const aPriority = a?.priority === 'needed' ? 0 : 1
        const bPriority = b?.priority === 'needed' ? 0 : 1
        if (aPriority !== bPriority) return aPriority - bPriority
        return (a?.display_name || a?.name) > (b?.display_name || b?.name) ? 1 : -1
      })
      measurementList = measurementList?.map((i: any) => { return { ...i, value: '' } })
      setMeasurement(measurementList)
      let currMeasurement = measurementList?.map((i: any) => {
        return { ...i, tags: (uiResource?.MEASUREMENT_TAGS)?.[i?.name] || [] }
      })
      setNewRecordList(currMeasurement)
    }).catch((error) => toast.error(Helper.getErrorText(error), Helper.bottom_center()))
  }

  const getRangeUnit = (value_range: any, unit: any) => {
    if (value_range?.min === null && value_range?.max === null)
      return unit ? <Typography component={'span'} color="textSecondary" variant="subtitle2">
        &nbsp;<small>({unit})</small>
      </Typography>
        : ''

    let vrmin = ''
    let vrmax = ''
    if (value_range?.min !== null) vrmin = value_range?.min
    if (value_range?.max !== null) vrmax = value_range?.max
    return <Typography component={'span'} color="textSecondary" variant="subtitle2">
      &nbsp;<small>({vrmin}-{vrmax}{unit ? ` ${unit}` : ''})</small>
    </Typography>
  }

  const updateNewRecordVal = (id: any, value: any) => {
    setNewRecordList((prev: any) =>
      prev.map((item: any) => item.id === id ? { ...item, value: value.toString(), date_recorded: dateRecorded } : item)
    )
  }

  const deleteMeasurement = (id: any) => setNewRecordList(newRecordList.filter((i: any) => id !== i.id))

  const handleAddsite = () => {
    setIsAddSite(true)
  }

  const renderMeasurementRow = (item: any, key: any) => (
    <Grid container spacing={0} alignItems={'center'} justifyItems={'center'} key={key} direction="row" className={classes.cardLabInfo}>
      <Grid item xs={8} className={`${classes.cardLabList} ${classes.breakWord}`}>
        <Typography className={classes.desc}>
          {formatDisplayName(item?.display_name) || item?.name}
          {getRangeUnit(item?.value_range, (item?.unit || item?.default_unit))}
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <Box className={classes.measurementInputOuter}>
          {item?.possible_dropdown_values?.length > 0
            ? <Select
              displayEmpty
              variant="standard"
              label='Status'
              className={classes.selectEmpty}
              inputProps={{ 'aria-label': 'With label' }}
              name="status"
              sx={{ width: '-webkit-fill-available', padding: '0px 0px 0px 5px', marginLeft: '0rem', fontSize: '0.7rem', }}
              onChange={(e) => updateNewRecordVal(item.id, e.target.value)}
            >
              {(item?.possible_dropdown_values)?.map((v: any, indx: any) => (
                <MenuItem key={`status-${indx}`}
                  className={classes.formControlItemsPadding}
                  value={typeof v === 'boolean' ? JSON.stringify(v) : v}
                >
                  <Typography className={classes.desc} sx={{ padding: '0px !important' }}>
                    {(typeof v === 'boolean' ? JSON.stringify(v) : v)}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
            : <TextField
              id={input?.name}
              variant="standard"
              type="text"
              sx={{ height: '26px' }}
              inputProps={{ sx: { textAlign: 'right', paddingRight: '0.3rem !important' } }}
              onChange={(e) => updateNewRecordVal(item.id, e.target.value)}
            />
          }
        </Box>
      </Grid>
      <Grid item xs sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <DeleteOutlinedIcon
          sx={{ fill: "var(--color-primary)" }}
          className={classes.addCircleButton}
          onClick={() => deleteMeasurement(item.id)}
        />
      </Grid>
    </Grid>
  )

  const handleMeasurement = () => {
    if (!measurement?.length) return <></>
    // Backend tags items with priority='needed' when patient_id+enrollment_id
    // are passed; if neither tag is present (legacy/no-priority case), all
    // items render as a single flat list.
    const hasPriority = measurement.some((m: any) => m?.priority === 'needed' || m?.priority === 'optional')
    const needed = hasPriority ? measurement.filter((m: any) => m?.priority === 'needed') : measurement
    const optional = hasPriority ? measurement.filter((m: any) => m?.priority !== 'needed') : []
    return (
      <Stack spacing={0.5} style={{ maxHeight: '25rem', width: '70%' }}>
        <Typography pl={0.2} variant='subtitle2' className={classes.taskInput}>Measurements</Typography>
        {needed.map((item: any, key: any) => renderMeasurementRow(item, `n-${key}`))}
        {optional.length > 0 && (
          <>
            <Button
              size="small"
              onClick={() => setShowOptionalMeasurements(!showOptionalMeasurements)}
              sx={{ alignSelf: 'flex-start', textTransform: 'none', fontSize: '0.75rem', padding: '2px 8px' }}
            >
              {showOptionalMeasurements ? `Hide other ${optional.length} measurements` : `Show all (+${optional.length})`}
            </Button>
            {showOptionalMeasurements && optional.map((item: any, key: any) => renderMeasurementRow(item, `o-${key}`))}
          </>
        )}
      </Stack>
    )
  }

  const handleTestStatus = (e: any) => {
    setResultData({ ...resultData, status: e.target.value === '' ? input.status : e.target.value })
    if (input.test_name !== '' && e.target.value) {
      const testType = e.target.value
      if (testType === 'results_available')
        getMeasurementByTest(input.test_name)
    }
  }

  const renderUploadResult = () => {
    // Map status keys to display labels
    const statusLabels: { [key: string]: string } = {
      'recommended': 'Recommended',
      'deferred': 'Deferred',
      'order_preparation': 'Order Preparation',
      'ordered': 'Ordered',
      'order_notified': 'Order Notified',
      'order_scheduled': 'Order Scheduled',
      'results_available': 'Results Available',
      'results_updated': 'Result Updated'
    }

    // Start with current status as the first option
    let statusData = [
      { key: input.status, label: statusLabels[input.status] || input.status }
    ] as any

    if (input.status === 'recommended' || input.status === 'deferred')
      statusData = [...statusData,
      { key: 'order_preparation', label: 'Order Preparation' },
      { key: 'ordered', label: 'Ordered' },
      { key: 'order_notified', label: 'Order Notified' },
      { key: 'order_scheduled', label: 'Order Scheduled' },
      { key: 'results_available', label: 'Result Available' },
      { key: 'results_updated', label: 'Result Updated' },
      { key: 'deferred', label: 'Deferred' },
      ].filter((item, index, self) => self.findIndex(i => i.key === item.key) === index)
    else if (input.status === 'order_preparation')
      statusData = [...statusData,
      { key: 'ordered', label: 'Ordered' },
      { key: 'order_notified', label: 'Order Notified' },
      { key: 'order_scheduled', label: 'Order Scheduled' },
      { key: 'results_available', label: 'Result Available' },
      { key: 'results_updated', label: 'Result Updated' },
      { key: 'deferred', label: 'Deferred' },
      ]
    else if (input.status === 'ordered') {
      statusData = [...statusData,
      { key: 'order_notified', label: 'Order Notified' },
      { key: 'order_scheduled', label: 'Order Scheduled' },
      { key: 'results_available', label: 'Result Available' },
      { key: 'results_updated', label: 'Result Updated' },
      { key: 'deferred', label: 'Deferred' },
      ]
    }
    else if (input.status === 'order_notified') {
      statusData = [...statusData,
      { key: 'order_scheduled', label: 'Order Scheduled' },
      { key: 'results_available', label: 'Result Available' },
      { key: 'results_updated', label: 'Result Updated' },
      { key: 'deferred', label: 'Deferred' },
      ]
    }
    else if (input.status === 'order_scheduled') {
      statusData = [...statusData,
      { key: 'results_available', label: 'Result Available' },
      { key: 'results_updated', label: 'Result Updated' },
      { key: 'deferred', label: 'Deferred' },
      ]
    }
    else if (input.status === 'results_available') {
      statusData = [...statusData,
      { key: 'results_updated', label: 'Result Updated' },
      { key: 'deferred', label: 'Deferred' },
      ]
    }

    return (
      <Stack width={'52%'} alignItems={'baseline'} justifyItems={'center'} spacing={1.2} direction="column">
        {input.isAdd && (
          <Box alignItems={'baseline'}>
            <Typography pl={0.2} variant="subtitle2" className={classes.taskInput}>
              Name
            </Typography>
            <Autocomplete
              id="add-name"
              options={testsList}
              getOptionLabel={(option: any) => formatDisplayName(option.display_name) || option.display_name}
              filterOptions={createFilterOptions({ matchFrom: 'any', stringify: (option: any) => formatDisplayName(option?.display_name) || option?.display_name })}
              // value={currTest}
              onChange={(e, selectedOption) => {
                if (selectedOption) {
                  setResultData({ ...resultData, test_name: selectedOption.key })
                  getMeasurementByTest(selectedOption.key)
                }
              }}
              disableClearable
              ListboxProps={{ sx: { '& .MuiAutocomplete-option': { padding: '2px 0px 2px 8px !important' } } }}
              sx={{ width: '9rem' }}
              renderInput={(params: any) => <TextField {...params} variant="standard" size="small" InputProps={{ ...params.InputProps }} />}
            />
          </Box>
        )}
        <Box>
          <TextField
            id="outlined-basic"
            name="test_notes"
            variant="outlined"
            placeholder="Add note here"
            size="small"
            value={note}
            inputProps={{ style: { fontSize: '0.7rem' } }}
            InputLabelProps={{ style: { fontSize: '0.7rem' } }}
            onBlur={() => setIsNoteFocused(false)}
            onFocus={() => setIsNoteFocused(true)}
            multiline={true}
            rows="2"
            onChange={(e: any) => setNote(e.target.value)}
            sx={{
              width: '20rem !important',
              '& .MuiOutlinedInput-root': { padding: '0.2rem 0rem 0.2rem 0.5rem !important' },
            }}
          />
        </Box>
        {input?.isAdd !== true && (
          <Stack direction="row" spacing={4} pt={0.3} justifyContent={'flex-start'}>
            <Box>
              <Typography variant="subtitle2" className={classes.taskInput}>
                Current status
              </Typography>
              <FormControl>
                <Select
                  displayEmpty
                  variant="standard"
                  label="Status"
                  readOnly={input.mark_status !== ''}
                  className={classes.selectEmpty}
                  inputProps={{ 'aria-label': 'With label' }}
                  value={resultData.status}
                  name="status"
                  sx={{ width: '8rem', padding: '0px', fontSize: '0.7rem' }}
                  onChange={(e: any) => handleTestStatus(e)}
                >
                  {statusData?.sort()?.map((item: any, index: any) => (
                    <MenuItem key={`status-${index}`} className={classes.formControlItemsPadding} value={item.key}>
                      <Typography className={classes.desc} sx={{ padding: '0px !important' }}>
                        {item.label}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Stack>
        )}
        {isControlVisible('test_select_site') && !selectedSite?.id && input?.isAdd !== true && resultData.status !== 'deferred' && (
          <Box pt={0.3}>
            <Button variant="outlined" size="small" sx={{ padding: '0.2rem 0.6rem !important' }} onClick={handleAddsite} startIcon={<LibraryAddIcon className={classes.editIcon} />}>
              Select site
            </Button>
          </Box>
        )}
        {/* Push a DRAFT order for this test into Elation (provider signs there). */}
        {isControlVisible('test_order_in_elation') && input?.id && input?.isAdd !== true && resultData.status !== 'deferred' && (
          <Box pt={0.3}>
            <Button variant="outlined" size="small" sx={{ padding: '0.2rem 0.6rem !important' }} disabled={pushingOrder} onClick={handlePushOrderToElation}>
              {pushingOrder ? 'Creating draft…' : 'Order in Elation'}
            </Button>
          </Box>
        )}
        {/* Display selected site details */}
        {selectedSite?.id && resultData.status !== 'deferred' && (
          <Box
            sx={{
              backgroundColor: 'var(--color-background-subtle)',
              borderRadius: 1,
              p: 1,
              mt: 0.5,
              border: '1px solid var(--color-border-light)',
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                  {selectedSite.name}
                </Typography>
                {(selectedSite.address || selectedSite.city || selectedSite.state) && (
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                    {[selectedSite.address, selectedSite.city, selectedSite.state, selectedSite.zip].filter(Boolean).join(', ')}
                  </Typography>
                )}
                {selectedSite.phone && (
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                    Phone: {selectedSite.phone}
                  </Typography>
                )}
                {selectedSite.fax && (
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                    Fax: {selectedSite.fax}
                  </Typography>
                )}
              </Box>
              <Stack direction="row" spacing={0.5} alignItems="center">
                {Helper.checkSouceDeatils(patientDetail?.source_details) && (
                  <Tooltip title="Sync to Elation">
                    <IconButton size="small" onClick={handleSyncSiteToElation} disabled={syncingSite} sx={{ p: 0.25 }}>
                      {syncingSite ? <CircularProgress size={14} /> : <CloudUploadIcon fontSize="small" color="primary" />}
                    </IconButton>
                  </Tooltip>
                )}
                <Button
                  size="small"
                  variant="text"
                  onClick={handleAddsite}
                  sx={{
                    minWidth: 'auto',
                    p: 0.5,
                    fontSize: '0.65rem',
                    color: 'var(--color-text-secondary)',
                    textTransform: 'none',
                  }}
                >
                  Change Site
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setSelectedSite(null)}
                  sx={{
                    minWidth: 'auto',
                    p: 0.5,
                    fontSize: '0.65rem',
                    color: 'var(--color-primary)',
                    textTransform: 'none',
                  }}
                >
                  Clear Site
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
        {input?.isAdd !== true && resultData.status !== 'recommended' && resultData.status !== 'deferred' && (
          <Stack direction="row" spacing={4} pt={0.3} justifyContent={'flex-start'}>
            <Box>
              <Typography pb={0.2} variant="subtitle2" className={classes.taskInput}>
                Ordered date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  className={classes.taskSchedule}
                  name={'date'}
                  format={'MM/DD/YYYY'}
                  formatDensity="dense"
                  slotProps={{
                    textField: { size: 'small', error: false },
                    actionBar: { actions: ['clear'] },
                  }}
                  value={moment(moment(completedDate.date, 'YYYY-MM-DD'), 'MM/DD/YYYY')}
                  onChange={(e: any) => validateDate(e, 'completedDate')}
                />
              </LocalizationProvider>
              {completedDate.errMsg && (
                <Typography color={'red'} className={classes.desc}>
                  {completedDate.errMsg}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography pb={0.2} variant="subtitle2" className={classes.taskInput}>
                Scheduled date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  className={classes.taskSchedule}
                  name={'date'}
                  format={'MM/DD/YYYY'}
                  formatDensity="dense"
                  slotProps={{
                    textField: { size: 'small', error: false },
                    actionBar: { actions: ['clear'] },
                  }}
                  value={moment(moment(scheduledDate.date, 'YYYY-MM-DD'), 'MM/DD/YYYY')}
                  onChange={(e: any) => validateDate(e, 'scheduledDate')}
                />
              </LocalizationProvider>
              {scheduledDate.errMsg && (
                <Typography color={'red'} className={classes.desc}>
                  {scheduledDate.errMsg}
                </Typography>
              )}
            </Box>
          </Stack>
        )}
        {['results_available'].includes(resultData.status) && (
          <Stack direction="row" spacing={4} pt={0.5} alignItems="flex-start">
            <Box>
              <Typography pb={0.2} variant="subtitle2" className={classes.taskInput}>
                Result date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  className={classes.taskSchedule}
                  name={'resultDate'}
                  format={'MM/DD/YYYY'}
                  formatDensity="dense"
                  slotProps={{
                    textField: { size: 'small', error: !!resultDate.errMsg },
                    actionBar: { actions: ['clear'] },
                  }}
                  value={resultDate.date ? moment(resultDate.date, 'YYYY-MM-DD') : null}
                  onChange={(e: any) => validateDate(e, 'resultDate')}
                />
              </LocalizationProvider>
              {resultDate.errMsg && (
                <Typography color={'red'} className={classes.desc}>
                  {resultDate.errMsg}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography pl={0.2} variant="subtitle2" className={classes.taskInput}>
                Result source
              </Typography>
              <FormControl className={classes.formControlStage}>
                <Select
                  id="source"
                  labelId="source"
                  variant="standard"
                  value={source}
                  onChange={(e) => setSource(e?.target?.value || '')}
                  sx={{ width: '5rem !important' }}
                  inputProps={{ classes: { icon: classes.icon } }}
                >
                  <MenuItem key={''} value={''} className={classes.programMenu}>{`  `}</MenuItem>
                  <MenuItem key={'external'} value={'external'} className={classes.programMenu}>
                    {'External'}
                  </MenuItem>
                  <MenuItem key={'internal'} value={'internal'} className={classes.programMenu}>
                    {'Internal'}
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Stack>
        )}
        {['order_preparation', 'ordered', 'order_notified', 'order_scheduled', 'order_follow_up'].includes(resultData.status) && (
          <Box>
            <Typography pl={0.2} pt={0.2} variant="subtitle2" className={classes.taskInput}>
              Insurance status
            </Typography>
            <FormControl className={classes.formControlStage}>
              <Select
                id="test-type"
                labelId="add-test-label"
                variant="standard"
                value={insuranceStatus}
                onChange={(e) => setInsuranceStatus(e?.target?.value || '')}
                sx={{ width: '8rem !important' }}
                inputProps={{ classes: { icon: classes.icon } }}
              >
                <MenuItem key={''} value={''} className={classes.programMenu}>{`  `}</MenuItem>
                <MenuItem key={'Approved'} value={'Approved'} className={classes.programMenu}>
                  {'Approved'}
                </MenuItem>
                <MenuItem key={'Rejected'} value={'Rejected'} className={classes.programMenu}>
                  {'Rejected'}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
        {['results_available'].includes(resultData.status) && (
          <Stack direction={'column'} pt={0.5} className={classes.fullWidth}>
            <Box pb={1}>
              <FormControl>
                <Input
                  inputProps={{ accept: '.pdf' }}
                  sx={{ position: 'absolute', display: 'none', padding: '0rem !important' }}
                  id="upload-file"
                  type="file"
                  name="inputFile"
                  onChange={handleFileChange}
                />
                <label htmlFor="upload-file">
                  <Button component="span" variant="outlined" size="small" sx={{ padding: '0.2rem !important', width: '5rem !important' }}>
                    Choose file
                  </Button>
                </label>
              </FormControl>
              {selectedFile && (
                <Typography variant="caption" pl={1.5}>
                  {' '}
                  {selectedFile?.name}
                </Typography>
              )}
            </Box>
            {handleMeasurement()}
          </Stack>
        )}
      </Stack>
    )
  }

  const updateTaskPanel = () => {
    dispatch(setSelectedTask({ ...selectedTask, isUpdate: true }))
  }

  const renderTestTask = () => {
    return (
      <Box width={'50%'} pr={1.5}>
        <PatientProgramTasksComponent
          input={{
            patient_id: patientDetail.id,
            isPrgTask: true,
            isTestTask: true,
            ref_id: input.id,
          }}
          updateTaskPanel={() => updateTaskPanel()}
        />
      </Box>
    )
  }
  return (
    <Draggable ref={uploadResultRef} defaultPosition={{ x: 0, y: 0 }} handle='.upload-test-dialog'>
      <Dialog
        id={'add-test'}
        fullWidth={true}
        maxWidth="md"
        open={open}
        onClose={handleDialogClose}
        aria-labelledby="Add test"
        hideBackdrop
        disableScrollLock
        onKeyUp={(e) => {
          if (e.key === 'Enter' && !isDisabled() && !isNoteFocused)
            handleSubmit(e)
        }}
        sx={{ position: "absolute", left: '15%', top: '20%' }}
      >
        <DialogTitle className={classes.addPatientQuestionHeaderSection} classes={{ root: 'upload-test-dialog' }}>
          <DialogTitleHeaderComponent title={input?.title} handleDialogClose={handleDialogClose} />
        </DialogTitle>
        <Box> {isLoading && <LinearProgress />} </Box>

        <DialogContent
          className={classes.addPatientDialogContent}
          sx={{ padding: `0.8rem 0.5rem 1rem 1.8rem !important`, minHeight: '18rem !important' }}
        >
          <Stack className={classes.fullWidth} direction="row" spacing={1} alignItems="baseline" justifyContent="flex-start">
            {renderUploadResult()}
            {input?.id && renderTestTask()}
          </Stack>
        </DialogContent>

        <Grid container spacing={2} className={classes.containerPadding} justifyContent="flex-end">
          <Grid item>
            <Button variant="outlined" size="small" disabled={isDisabled()} onClick={handleSubmit} component="label" className={isDisabled() ? classes.disabledBtn : ''}>
              Submit
            </Button>
          </Grid>
          <Grid item>
            <Button variant="outlined" size="small" onClick={handleDialogClose}>
              Cancel
            </Button>
          </Grid>
        </Grid>

        {isAddSite &&
          <SiteListComponent
            open={isAddSite}
            item={{
              title: 'Select Site',
              isAddSite: true,
              isSelectMode: true,
            }}
            handleDialogClose={() => setIsAddSite(false)}
            onSelectSite={async (site: any) => {
              // Fetch linked phone numbers for the site
              try {
                const phonesRes = await patientService.getSitePhones(site.id)
                const phones = phonesRes.data?.phones || []
                const phoneNumber = phones.find((p: any) => p.number_type === 'phone')?.number
                const faxNumber = phones.find((p: any) => p.number_type === 'fax')?.number
                setSelectedSite({
                  ...site,
                  phone: phoneNumber || site.phone,
                  fax: faxNumber || site.fax,
                })
              } catch (err) {
                // Fall back to site's own phone/fax if fetch fails
                setSelectedSite(site)
              }
              setIsAddSite(false)
            }}
          />
        }
      </Dialog>
    </Draggable>
  );
};

export default withStyles(stylesheet)(UploadTestResultComponent)